/**
 * Cancellation service -- preview refund and cancel bookings.
 *
 * Singleton pattern. Throws ServiceError on failure.
 * Uses pricing.ts calculateRefund() for full/partial/none tiers.
 */
// Per-function entry — the `date-fns` root barrel re-exports the whole
// library and Metro has no tree shaking. See lib/utils/dates.ts.
import { differenceInHours } from 'date-fns/differenceInHours'
import { supabase } from '../supabase'
import { ServiceError } from '../errors/service-error'
import { calculateRefund } from '../utils/pricing'
import { rentalDaysBetween } from '../utils/dates'
import { captureError, addBreadcrumb } from '../utils/observability'
import { respondToPaidRental } from './host-approval.service'
import type {
  CancellationPreview,
  CancelBookingResult,
  CancellationReason,
  RefundReconciliationStatus,
} from '../types/cancellation.types'

// ── Row interface ───────────────────────────────────────────────────────────

interface RentalRow {
  id: string
  status: string
  guest_id: string
  host_id: string
  created_at: string
  start_at: string
  end_at: string
  total_cents: number
  subtotal_cents: number
  platform_fee_cents: number
  insurance_fee_cents: number
  security_deposit_cents: number | null
  cancellation_fee_cents: number | null
  cancelled_at: string | null
  cancelled_by: string | null
  cancellation_reason: string | null
}

const CANCELLABLE_STATUSES = [
  'pending_payment',
  'awaiting_host',
  'confirmed',
  'pending_pickup',
] as const

interface RefundFunctionResponse {
  refunded?: boolean
  manualRequired?: boolean
  alreadyProcessing?: boolean
  intendedRefundCents?: number
  amountCents?: number
  policy?: string
  reason?: string
  error?: string
}

// ── Service ─────────────────────────────────────────────────────────────────

class CancellationServiceImpl {
  /**
   * Preview what happens if a booking is cancelled right now.
   */
  async previewCancellation(bookingId: string): Promise<CancellationPreview> {
    const rental = await this.fetchRental(bookingId)

    const startDate = new Date(rental.start_at)
    const endDate = new Date(rental.end_at)
    const now = new Date()
    const hoursBeforeStart = Math.max(0, differenceInHours(startDate, now))
    const totalDays = rentalDaysBetween(startDate, endDate)

    // Host cancellations refund the guest 100% (fee included); guest
    // cancellations follow the tiers and keep the fee. Preview from the
    // viewer's own role.
    const { data: sessionData } = await supabase.auth.getSession()
    const viewerId = sessionData.session?.user?.id
    const cancelledBy: 'guest' | 'host' = viewerId === rental.host_id ? 'host' : 'guest'
    // Derecho de retracto (LPC Art. 13-A): full refund incl. fee within 8 days
    // of booking and before the trip starts.
    const hoursSinceBooking = Math.max(0, differenceInHours(now, new Date(rental.created_at)))

    const refund = calculateRefund({
      totalCents: rental.total_cents,
      guestServiceFeeCents: rental.platform_fee_cents,
      insuranceCents: rental.insurance_fee_cents,
      depositCents: rental.security_deposit_cents ?? 0,
      hoursBeforeStart,
      daysUsed: 0,
      totalDays,
      cancelledBy,
      hoursSinceBooking,
    })

    const cancellationFeeCents =
      rental.status === 'awaiting_host' ? 0 : rental.total_cents - refund.refundCents

    return {
      bookingId: rental.id,
      policy: refund.policy,
      refundCents: rental.status === 'awaiting_host' ? rental.total_cents : refund.refundCents,
      cancellationFeeCents,
      hoursBeforeStart,
    }
  }

  /**
   * Cancel a booking with reason. Computes refund and updates rental row.
   */
  async cancelBooking(
    userId: string,
    bookingId: string,
    reason: CancellationReason | string,
  ): Promise<CancelBookingResult> {
    const rental = await this.fetchRental(bookingId)

    // Authorization
    const isGuest = rental.guest_id === userId
    const isHost = rental.host_id === userId
    if (!isGuest && !isHost) {
      throw ServiceError.unauthorized('Not authorized to cancel this booking')
    }

    // Status check
    if (!CANCELLABLE_STATUSES.includes(rental.status as (typeof CANCELLABLE_STATUSES)[number])) {
      throw ServiceError.validation('This booking cannot be cancelled', {
        currentStatus: rental.status,
      })
    }

    if (isHost && rental.status === 'awaiting_host') {
      await respondToPaidRental(bookingId, 'decline')
      return {
        bookingId,
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
        cancelledBy: 'host',
        reason: 'host_declined_paid_booking',
        refundCents: rental.total_cents,
        cancellationFeeCents: 0,
        refundStatus: 'processing',
      }
    }

    // Compute refund
    const startDate = new Date(rental.start_at)
    const endDate = new Date(rental.end_at)
    const now = new Date()
    const hoursBeforeStart = Math.max(0, differenceInHours(startDate, now))
    const totalDays = rentalDaysBetween(startDate, endDate)

    const cancelledBy: 'guest' | 'host' = isGuest ? 'guest' : 'host'
    // Derecho de retracto (LPC Art. 13-A): full refund incl. fee within 8 days
    // of booking and before the trip starts.
    const hoursSinceBooking = Math.max(0, differenceInHours(now, new Date(rental.created_at)))
    const refund = calculateRefund({
      totalCents: rental.total_cents,
      guestServiceFeeCents: rental.platform_fee_cents,
      insuranceCents: rental.insurance_fee_cents,
      depositCents: rental.security_deposit_cents ?? 0,
      hoursBeforeStart,
      daysUsed: 0,
      totalDays,
      cancelledBy,
      hoursSinceBooking,
    })

    const cancellationFeeCents =
      rental.status === 'awaiting_host' ? 0 : rental.total_cents - refund.refundCents
    const cancelledAt = now.toISOString()

    // `cancellation_fee_cents` NO se escribe acá: es una columna server-owned
    // (`private.assert_rental_money_is_server_owned`) y el UPDATE del cliente
    // rebotaba con 42501 — "Los montos de una reserva no se editan desde la
    // aplicación" (TAGO-MOBILE-4V). El fee lo persiste `refund-payment`, que ya
    // recalcula la política con los mismos datos y corre con service_role.
    const { data: updated, error: updateError } = await supabase
      .from('rentals')
      .update({
        status: 'cancelled',
        cancelled_at: cancelledAt,
        cancelled_by: cancelledBy,
        cancellation_reason: reason,
      })
      .eq('id', bookingId)
      .in('status', [...CANCELLABLE_STATUSES])
      .select(
        'id, status, guest_id, host_id, created_at, start_at, end_at, total_cents, subtotal_cents, platform_fee_cents, insurance_fee_cents, security_deposit_cents, cancellation_fee_cents, cancelled_at, cancelled_by, cancellation_reason',
      )
      .maybeSingle()

    if (updateError) throw ServiceError.fromSupabaseError(updateError)
    if (!updated) return this.resolveAlreadyTransitionedCancellation(userId, bookingId, reason)

    const refundResult = await this.reconcileRefund(bookingId, cancelledBy)

    return {
      bookingId,
      status: 'cancelled',
      cancelledAt,
      cancelledBy,
      reason,
      refundCents: rental.status === 'awaiting_host' ? rental.total_cents : refund.refundCents,
      cancellationFeeCents,
      ...refundResult,
    }
  }

  // ── Private ─────────────────────────────────────────────────────────────

  private async fetchRental(bookingId: string): Promise<RentalRow> {
    const { data, error } = await supabase
      .from('rentals')
      .select(
        'id, status, guest_id, host_id, created_at, start_at, end_at, total_cents, subtotal_cents, platform_fee_cents, insurance_fee_cents, security_deposit_cents, cancellation_fee_cents, cancelled_at, cancelled_by, cancellation_reason',
      )
      .eq('id', bookingId)
      .maybeSingle()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) throw ServiceError.notFound('Booking')
    return data as RentalRow
  }

  private async resolveAlreadyTransitionedCancellation(
    userId: string,
    bookingId: string,
    reason: CancellationReason | string,
  ): Promise<CancelBookingResult> {
    const latest = await this.fetchRental(bookingId)
    const isGuest = latest.guest_id === userId
    const isHost = latest.host_id === userId
    if (!isGuest && !isHost) {
      throw ServiceError.unauthorized('Not authorized to cancel this booking')
    }
    if (latest.status !== 'cancelled') {
      throw ServiceError.validation('This booking cannot be cancelled', {
        currentStatus: latest.status,
      })
    }

    const refund = this.calculatePreviewFromRental(latest)
    const cancelledBy = latest.cancelled_by === 'host' ? 'host' : 'guest'
    const refundResult = await this.reconcileRefund(bookingId, cancelledBy)

    return {
      bookingId,
      status: 'cancelled',
      cancelledAt: latest.cancelled_at ?? new Date().toISOString(),
      cancelledBy,
      reason: latest.cancellation_reason ?? reason,
      refundCents: refund.refundCents,
      cancellationFeeCents:
        latest.cancellation_fee_cents ?? latest.total_cents - refund.refundCents,
      ...refundResult,
    }
  }

  private calculatePreviewFromRental(rental: RentalRow) {
    const startDate = new Date(rental.start_at)
    const endDate = new Date(rental.end_at)
    const cancelledAt = rental.cancelled_at ? new Date(rental.cancelled_at) : new Date()
    const hoursBeforeStart = Math.max(0, differenceInHours(startDate, cancelledAt))
    const totalDays = rentalDaysBetween(startDate, endDate)
    // Derecho de retracto (LPC Art. 13-A): hours from booking to cancellation.
    const hoursSinceBooking = Math.max(
      0,
      differenceInHours(cancelledAt, new Date(rental.created_at)),
    )

    return calculateRefund({
      totalCents: rental.total_cents,
      guestServiceFeeCents: rental.platform_fee_cents,
      insuranceCents: rental.insurance_fee_cents,
      depositCents: rental.security_deposit_cents ?? 0,
      hoursBeforeStart,
      daysUsed: 0,
      totalDays,
      cancelledBy:
        rental.cancelled_by === 'host' || rental.cancelled_by === 'platform'
          ? (rental.cancelled_by as 'host' | 'platform')
          : 'guest',
      hoursSinceBooking,
    })
  }

  private async reconcileRefund(
    bookingId: string,
    cancelledBy: 'guest' | 'host',
  ): Promise<{
    refundStatus: RefundReconciliationStatus
    refundMessage?: string
    manualRequired?: boolean
  }> {
    try {
      addBreadcrumb('booking', 'refund-payment invoked after cancellation', {
        bookingId,
        cancelledBy,
      })
      const { data, error } = await supabase.functions.invoke<RefundFunctionResponse>(
        'refund-payment',
        {
          body: { rentalId: bookingId },
        },
      )

      if (error) {
        captureError(
          error,
          { service: 'cancellation', action: 'refund-payment' },
          { bookingId, cancelledBy },
        )
        return {
          refundStatus: 'pending_reconciliation',
          refundMessage: error.message,
          manualRequired: true,
        }
      }

      return this.resolveRefundStatus(data)
    } catch (err) {
      captureError(
        err,
        { service: 'cancellation', action: 'refund-payment' },
        { bookingId, cancelledBy },
      )
      return {
        refundStatus: 'pending_reconciliation',
        refundMessage: err instanceof Error ? err.message : String(err),
        manualRequired: true,
      }
    }
  }

  private resolveRefundStatus(data: RefundFunctionResponse | null): {
    refundStatus: RefundReconciliationStatus
    refundMessage?: string
    manualRequired?: boolean
  } {
    if (!data) return { refundStatus: 'pending_reconciliation', manualRequired: true }
    if (data.refunded) return { refundStatus: 'refunded', refundMessage: data.reason }
    if (data.alreadyProcessing) return { refundStatus: 'processing', refundMessage: data.reason }
    if (data.manualRequired) {
      return {
        refundStatus: 'manual_required',
        refundMessage: data.reason,
        manualRequired: true,
      }
    }
    if ((data.intendedRefundCents ?? 0) <= 0) {
      return { refundStatus: 'not_applicable', refundMessage: data.reason }
    }
    return {
      refundStatus: 'pending_reconciliation',
      refundMessage: data.reason ?? data.error,
      manualRequired: true,
    }
  }
}

export const cancellationService = new CancellationServiceImpl()
